import React, { useEffect, useState } from 'react';
import apiClient from '../apiClient';

interface AvailabilityItem {
  id: number;
  auditor_id: number;
  date: string; // YYYY-MM-DD
  is_available: boolean;
}

interface AuditorOption {
  id: number;
  username: string;
}

const emptyForm: {
  id: number | null;
  date: string;
  is_available: boolean;
} = {
  id: null,
  date: '',
  is_available: true,
};

interface MonthViewResponse {
  auditor_id: number;
  year: number;
  month: number;
  days: { [date: string]: boolean };
}

const AuditorAvailabilityPage: React.FC = () => {
  const [items, setItems] = useState<AvailabilityItem[]>([]);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditors, setAuditors] = useState<AuditorOption[]>([]);
  const [selectedAuditorId, setSelectedAuditorId] = useState<number | ''>('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [monthView, setMonthView] = useState<MonthViewResponse | null>(null);
  const [selectedDates, setSelectedDates] = useState<string[]>([]);

  const loadAvailability = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<{ data: { items: AvailabilityItem[] } }>(
        '/auditor-availability',
        {
          params:
            selectedAuditorId !== ''
              ? { auditor_id: selectedAuditorId }
              : undefined,
        }
      );
      setItems(res.data.data.items);
    } catch (e) {
      setError('No se pudo cargar la disponibilidad');
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (delta: number) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    }
    setYear(newYear);
    setMonth(newMonth);
  };

  const renderMonthGrid = () => {
    if (!monthView) {
      return <p>No se pudo cargar la vista mensual.</p>;
    }

    const firstDay = new Date(monthView.year, monthView.month - 1, 1);
    // 0-6, domingo=0 -> lo convertimos a 0=lunes, 6=domingo
    const startWeekday = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(monthView.year, monthView.month, 0).getDate();

    const cells: React.ReactNode[] = [];
    for (let i = 0; i < startWeekday; i += 1) {
      cells.push(<div key={`empty-${i}`} />);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dateStr = `${monthView.year}-${String(monthView.month).padStart(2, '0')}-${String(
        day
      ).padStart(2, '0')}`;
      const isAvailable = monthView.days[dateStr] ?? false;
      const isSelected = selectedDates.includes(dateStr);

      cells.push(
        <div
          key={dateStr}
          style={{
            border: '1px solid rgba(55, 65, 81, 0.9)',
            padding: 4,
            minHeight: 40,
            backgroundColor: isAvailable
              ? 'rgba(22, 163, 74, 0.18)'
              : 'rgba(239, 68, 68, 0.16)',
            boxShadow: isSelected
              ? '0 0 0 1px rgba(56, 189, 248, 0.8)'
              : '0 0 0 0 rgba(0,0,0,0)',
            fontSize: 12,
            cursor: 'pointer',
            borderRadius: 8,
          }}
          onClick={() => {
            setError(null);
            setForm(prev => ({
              ...prev,
              date: dateStr,
              // si el día ya tiene registro usamos ese valor, si no conservamos el del formulario
              is_available: monthView.days.hasOwnProperty(dateStr)
                ? isAvailable
                : prev.is_available,
            }));
            setSelectedDates(prev =>
              prev.includes(dateStr)
                ? prev.filter(d => d !== dateStr)
                : [...prev, dateStr]
            );
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: 2 }}>{day}</div>
          <div style={{ fontSize: 11, color: isAvailable ? '#bbf7d0' : '#fecaca' }}>
            {isAvailable ? 'Disponible' : 'No disp.'}
          </div>
        </div>
      );
    }

    return (
      <div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 4,
            marginBottom: 4,
            fontSize: 11,
            color: '#9ca3af',
          }}
        >
          {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(dia => (
            <div key={dia} style={{ textAlign: 'center' }}>
              {dia}
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 4,
          }}
        >
          {cells}
        </div>
      </div>
    );
  };

  const loadMonthView = async (targetYear: number, targetMonth: number) => {
    try {
      const res = await apiClient.get<{ data: MonthViewResponse }>(
        '/auditor-availability/month-view',
        {
          params:
            selectedAuditorId !== ''
              ? { year: targetYear, month: targetMonth, auditor_id: selectedAuditorId }
              : { year: targetYear, month: targetMonth },
        }
      );
      setMonthView(res.data.data);
    } catch (e) {
      // Si falla, simplemente no mostramos calendario.
      setMonthView(null);
    }
  };

  useEffect(() => {
    const loadAuditors = async () => {
      try {
        const res = await apiClient.get<{ data: { items: AuditorOption[] } }>(
          '/users',
          { params: { per_page: 100 } }
        );
        setAuditors(res.data.data.items);
      } catch (e) {
        // Ignoramos error, el selector de auditor es auxiliar
      }
    };

    loadAuditors();
    loadAvailability();
  }, []);

  useEffect(() => {
    loadMonthView(year, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, selectedAuditorId]);

  const startCreate = () => {
    setError(null);
    setForm(emptyForm);
  };

  const startEdit = (item: AvailabilityItem) => {
    setError(null);
    setForm({
      id: item.id,
      date: item.date,
      is_available: item.is_available,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    const { name } = target;

    let value: string | boolean = target.value;
    if (target.type === 'checkbox') {
      value = target.checked;
    }

    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Si hay selección múltiple, usamos bulk-set; si no, usamos la fecha del formulario.
    const datesToSet = selectedDates.length > 0
      ? selectedDates
      : form.date
      ? [form.date]
      : [];

    if (datesToSet.length === 0) {
      setError('Selecciona al menos una fecha');
      setLoading(false);
      return;
    }

    try {
      await apiClient.post('/auditor-availability/bulk-set', {
        dates: datesToSet,
        is_available: form.is_available,
        auditor_id: selectedAuditorId !== '' ? selectedAuditorId : undefined,
      });
      setForm(emptyForm);
      setSelectedDates([]);
      await loadAvailability();
      await loadMonthView(year, month);
    } catch (e) {
      setError('Error al guardar la disponibilidad (revisa fechas y permisos)');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('¿Eliminar este registro de disponibilidad?')) return;

    setLoading(true);
    setError(null);
    try {
      await apiClient.delete(`/auditor-availability/${id}`);
      await loadAvailability();
    } catch (e) {
      setError('No se pudo eliminar la disponibilidad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#020617',
        padding: '8px 8px 24px',
        color: '#e5e7eb',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      }}
    >
      <header style={{ marginBottom: 16 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: 0.3,
            color: '#f9fafb',
          }}
        >
          Disponibilidad del auditor
        </h1>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            color: '#9ca3af',
          }}
        >
          Configura qué días está disponible cada auditor y revisa su calendario mensual.
        </p>
      </header>

      {/* Selector de auditor */}
      <section
        style={{
          marginBottom: 16,
          padding: 14,
          borderRadius: 18,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          border: '1px solid rgba(148, 163, 184, 0.35)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Auditor</span>
            <select
              value={selectedAuditorId}
              onChange={e => {
                const value = e.target.value;
                setSelectedAuditorId(value === '' ? '' : Number(value));
              }}
              style={{
                minWidth: 180,
                padding: '6px 8px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                color: '#e5e7eb',
                fontSize: 12,
                outline: 'none',
              }}
            >
              <option value="">Yo mismo</option>
              {auditors.map(a => (
                <option key={a.id} value={a.id}>
                  {a.username} (#{a.id})
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 2fr)',
          gap: 16,
          alignItems: 'flex-start',
        }}
      >
        {/* Formulario y listado */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              {form.id == null ? 'Nuevo día de disponibilidad' : `Editar día #${form.id}`}
            </h2>
            <button
              type="button"
              onClick={startCreate}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.7)',
                backgroundColor: 'transparent',
                color: '#e5e7eb',
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              Nuevo
            </button>
          </div>

          <form onSubmit={handleSubmit} style={{ fontSize: 12 }}>
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: 4,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Fecha
              </label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.6)',
                  backgroundColor: 'rgba(15, 23, 42, 0.85)',
                  color: '#e5e7eb',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                }}
              >
                <input
                  type="checkbox"
                  name="is_available"
                  checked={form.is_available}
                  onChange={handleChange}
                  style={{ cursor: 'pointer' }}
                />
                Disponible
              </label>
            </div>
            {error && (
              <div
                style={{
                  color: '#fecaca',
                  background: 'rgba(220, 38, 38, 0.12)',
                  border: '1px solid rgba(248, 113, 113, 0.4)',
                  borderRadius: 10,
                  padding: '6px 8px',
                  fontSize: 12,
                  marginBottom: 8,
                }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: 4,
                padding: '8px 14px',
                borderRadius: 999,
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'default' : 'pointer',
                color: '#0b1120',
                background:
                  loading
                    ? 'linear-gradient(to right, #6b7280, #4b5563)'
                    : 'linear-gradient(135deg, #22c55e, #16a34a, #22c55e)',
                boxShadow: loading
                  ? '0 0 0 rgba(0,0,0,0)'
                  : '0 12px 30px rgba(22, 163, 74, 0.55)',
                opacity: loading ? 0.9 : 1,
              }}
            >
              {loading ? 'Guardando...' : 'Guardar disponibilidad'}
            </button>
          </form>

          <div style={{ marginTop: 18 }}>
            <h3
              style={{
                margin: '0 0 8px',
                fontSize: 13,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Listado de días
            </h3>
            {loading && <p style={{ fontSize: 12 }}>Cargando...</p>}
            {!loading && items.length === 0 && (
              <p style={{ fontSize: 12, color: '#9ca3af' }}>
                No hay registros de disponibilidad.
              </p>
            )}
            {!loading && items.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 12,
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        textAlign: 'left',
                        borderBottom: '1px solid rgba(55, 65, 81, 0.8)',
                      }}
                    >
                      <th style={{ padding: '6px 4px' }}>ID</th>
                      <th style={{ padding: '6px 4px' }}>Fecha</th>
                      <th style={{ padding: '6px 4px' }}>Disponible</th>
                      <th style={{ padding: '6px 4px' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(item => (
                      <tr
                        key={item.id}
                        style={{ borderBottom: '1px solid rgba(31, 41, 55, 0.9)' }}
                      >
                        <td style={{ padding: '6px 4px' }}>{item.id}</td>
                        <td style={{ padding: '6px 4px' }}>{item.date}</td>
                        <td style={{ padding: '6px 4px' }}>
                          {item.is_available ? 'Sí' : 'No'}
                        </td>
                        <td style={{ padding: '6px 4px' }}>
                          <button
                            type="button"
                            onClick={() => startEdit(item)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              border: '1px solid rgba(148, 163, 184, 0.7)',
                              backgroundColor: 'transparent',
                              color: '#e5e7eb',
                              fontSize: 11,
                              cursor: 'pointer',
                              marginRight: 4,
                            }}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              border: '1px solid rgba(248, 113, 113, 0.7)',
                              backgroundColor: 'rgba(220, 38, 38, 0.15)',
                              color: '#fecaca',
                              fontSize: 11,
                              cursor: 'pointer',
                            }}
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* Vista mensual */}
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 15,
                fontWeight: 600,
                color: '#e5e7eb',
              }}
            >
              Vista mensual
            </h2>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
              }}
            >
              <button
                type="button"
                onClick={() => changeMonth(-1)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.7)',
                  backgroundColor: 'transparent',
                  color: '#e5e7eb',
                  cursor: 'pointer',
                }}
              >
                {'<'}
              </button>
              <span>
                {year}-{String(month).padStart(2, '0')}
              </span>
              <button
                type="button"
                onClick={() => changeMonth(1)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 999,
                  border: '1px solid rgba(148, 163, 184, 0.7)',
                  backgroundColor: 'transparent',
                  color: '#e5e7eb',
                  cursor: 'pointer',
                }}
              >
                {'>'}
              </button>
            </div>
          </div>
          {renderMonthGrid()}
        </section>
      </div>
    </div>
  );
} 

export default AuditorAvailabilityPage;
